import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { getCloudflareDownloadUrl } from 'store/1wayteachervideo/thunks';

const CLOUDFLARE_FILENAME_VALUE_MAX = 120;

export const getVideoDownloadDateTimeStamp = () => dayjs().format('YYYY-MM-DD_HHmmss');

export const appendVideoDownloadDateTimeToFileName = (baseFileName, stamp, index, total) => {
    const sequencePart = total > 1 ? `_${index + 1}` : '';
    const insertion = `_${stamp}${sequencePart}`;
    const dot = baseFileName.lastIndexOf('.');
    if (dot <= 0) {
        return `${baseFileName}${insertion}`;
    }
    return `${baseFileName.slice(0, dot)}${insertion}${baseFileName.slice(dot)}`;
};

const stripVideoExtension = (name) => name.replace(/\.(mp4|webm|mov|m4v|ogg)$/i, '');

const toCloudflareStreamFilenameQueryValue = (downloadFileName) => {
    const base = stripVideoExtension(downloadFileName);
    const sanitized = base
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    const trimmed = sanitized.slice(0, CLOUDFLARE_FILENAME_VALUE_MAX);
    return trimmed.length > 0 ? trimmed : 'video';
};

export const buildCloudflareStreamDownloadUrlWithPreferredFileName = (downloadUrl, downloadFileName) => {
    const value = toCloudflareStreamFilenameQueryValue(downloadFileName);
    const sep = downloadUrl.includes('?') ? '&' : '?';
    return `${downloadUrl}${sep}filename=${value}`;
};

export const triggerBrowserDownloadFromHref = (href) => {
    const link = document.createElement('a');
    link.href = href;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export async function saveRemoteMediaWithPreferredName(fileUrl, downloadFileName) {
    try {
        const response = await fetch(fileUrl, {
            mode: 'cors',
            credentials: 'omit',
            cache: 'no-store',
        });
        if (!response.ok) {
            throw new Error(`Download failed (${response.status})`);
        }
        const blob = await response.blob();
        saveAs(blob, downloadFileName);
    } catch (error) {
        Sentry.captureException(error);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = downloadFileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

export const isCloudflareUrl = (url) =>
    url && (url.includes('cloudflarestream.com') || url.includes('.m3u8'));

export const extractCloudflareVideoId = (url) => {
    try {
        if (url.includes('cloudflarestream.com')) {
            const match = url.match(/cloudflarestream\.com\/([a-zA-Z0-9]+)/);
            return match ? match[1] : null;
        }
        if (url.includes('.m3u8')) {
            const match = url.match(/\/([a-zA-Z0-9]+)\/manifest\/video\.m3u8/);
            return match ? match[1] : null;
        }
        return null;
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
};

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

const CLOUDFLARE_DOWNLOAD_UNAVAILABLE_MESSAGE =
    'MP4 download is not available for this video yet. Wait for encoding to finish, then try again.';

export const getCloudflareDownloadUnavailableMessage = (response) => {
    if (response?.status !== 'unavailable') {
        return null;
    }
    return response.error || CLOUDFLARE_DOWNLOAD_UNAVAILABLE_MESSAGE;
};

export async function pollCloudflareMp4DownloadUrl({ videoId, showAlert, quiet = false, onProgress }) {
    const run = (status) => {
        if (typeof onProgress === 'function') {
            onProgress(status);
        }
    };

    let downloadUrl = null;
    let attempts = 0;

    while (!downloadUrl && attempts < MAX_POLL_ATTEMPTS) {
        try {
            const response = await getCloudflareDownloadUrl(videoId);
            const unavailableMessage = getCloudflareDownloadUnavailableMessage(response);
            if (unavailableMessage) {
                const unavailableError = new Error(unavailableMessage);
                unavailableError.cloudflareDownloadUnavailable = true;
                throw unavailableError;
            }
            downloadUrl = response.mp4Url || response.downloadUrl;

            if (downloadUrl && response.status === 'ready') {
                run('ready');
                if (!quiet && showAlert) {
                    showAlert('Download ready! Starting download...', 'success');
                }
                return downloadUrl;
            }
            if (response.status === 'pending' || !downloadUrl) {
                attempts += 1;
                if (attempts < MAX_POLL_ATTEMPTS) {
                    run('processing');
                    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
                }
            }
        } catch (error) {
            if (error?.cloudflareDownloadUnavailable) {
                throw error;
            }
            if (attempts >= MAX_POLL_ATTEMPTS - 1) {
                throw error;
            }
            attempts += 1;
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
    }

    if (!downloadUrl) {
        throw new Error('Download is still being processed. Please try again in a few moments.');
    }
    return downloadUrl;
}

export async function downloadCloudflareVideoWithPolling({
    videoId,
    downloadFileName,
    showAlert,
    quiet = false,
    onProgress,
}) {
    const downloadUrl = await pollCloudflareMp4DownloadUrl({
        videoId,
        showAlert,
        quiet,
        onProgress,
    });
    await new Promise((resolve) => setTimeout(resolve, 500));
    const href = buildCloudflareStreamDownloadUrlWithPreferredFileName(downloadUrl, downloadFileName);
    triggerBrowserDownloadFromHref(href);
}

export async function downloadVideoLikePreview({
    url,
    fileName = 'video.mp4',
    showAlert,
    quiet = false,
    onProgress,
}) {
    if (!url) {
        return;
    }

    const run = (status) => {
        if (typeof onProgress === 'function') {
            onProgress(status);
        }
    };

    if (isCloudflareUrl(url)) {
        const videoId = extractCloudflareVideoId(url);
        if (videoId) {
            run('preparing');
            if (!quiet && showAlert) {
                showAlert('Preparing download... This may take a few moments.', 'info');
            }
            await downloadCloudflareVideoWithPolling({
                videoId,
                downloadFileName: fileName || `video-${videoId}.mp4`,
                showAlert,
                quiet,
                onProgress,
            });
            return;
        }
    }

    run('ready');
    await saveRemoteMediaWithPreferredName(url, fileName);
}
